import React, { useMemo } from "react";
import { RULES_DATA } from "../../layout/Rules";
import "./MatchRulesModal.css";

const parseMatchRule = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return null;

  // Expected pattern:
  // "FOOTBALL Match Odds: ...", "TENNIS Match Odds: ...", "CRICKET Match Odds: ..."
  // or "FOOTBALL Under_Over Goals: ..."
  const idx = raw.indexOf(":");
  const left = idx >= 0 ? raw.slice(0, idx).trim() : raw;
  const body = idx >= 0 ? raw.slice(idx + 1).trim() : "";

  const parts = left.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;

  const sport = parts[0].toUpperCase();
  const market = parts.slice(1).join(" ");

  return { sport, market, body };
};

const MatchRulesModal = ({ show, onClose, sectionHeading = "Match" }) => {
  const groups = useMemo(() => {
    const target = String(sectionHeading || "Match").toLowerCase();
    const matchSection = Array.isArray(RULES_DATA)
      ? RULES_DATA.find((s) => String(s?.heading).toLowerCase() === target)
      : null;

    // For Match: parse into sport groups (CRICKET/TENNIS/FOOTBALL) like live modal
    if (target === "match") {
      const rows = (matchSection?.rules || [])
        .map((r) => parseMatchRule(r?.text))
        .filter(Boolean);

      const bySport = new Map();
      rows.forEach((r) => {
        if (!bySport.has(r.sport)) bySport.set(r.sport, []);
        bySport.get(r.sport).push(r);
      });

      // Keep ordering similar to the live modal (Cricket, Tennis, Football)
      const order = ["CRICKET", "TENNIS", "FOOTBALL"];
      const ordered = [];
      order.forEach((s) => {
        if (bySport.has(s)) ordered.push([s, bySport.get(s)]);
      });
      bySport.forEach((val, key) => {
        if (!order.includes(key)) ordered.push([key, val]);
      });

      return { mode: "grouped", grouped: ordered, plain: [] };
    }

    // For other sections (e.g. Bookmaker): show plain list
    const plain = (matchSection?.rules || [])
      .map((r) => ({
        text: String(r?.text || "").trim(),
        // Keep danger flag available if we ever need it, but design-wise
        // we won't force red styling in the modal.
        danger: !!r?.danger,
      }))
      .filter((r) => !!r.text);

    return { mode: "plain", grouped: [], plain };
  }, [sectionHeading]);

  if (!show) return null;

  const headerTitle =
    String(sectionHeading || "").toLowerCase() === "bookmaker"
      ? "Bookmaker Rules"
      : "Match Rules";

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal match-rules-modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{headerTitle}</h5>
              <button
                type="button"
                aria-label="Close"
                className="close-btn"
                onClick={onClose}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="info-modal-body">
                <div>
                  {groups.mode === "grouped" &&
                    groups.grouped.map(([sport, items]) => (
                      <React.Fragment key={sport}>
                        <h5>{sport}</h5>
                        {items.map((it, idx) => (
                          <p key={`${sport}-${idx}`}>
                            <b>{it.market}</b> {" :- "}
                            {it.body}
                          </p>
                        ))}
                      </React.Fragment>
                    ))}

                  {groups.mode === "plain" && (
                    <>
                      {groups.plain.map((r, idx) => (
                        <p key={`${sectionHeading}-${idx}`}>
                          <span className={r.danger ? "text-danger" : undefined}>
                            {r.text}
                          </span>
                        </p>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchRulesModal;

